import { AppDispatch } from '@/store';
import {
  missingItemFormLoading,
  userMissingItemFormsLoading,
  fetchUserMissingItemFormsSuccess,
  fetchUserMissingItemFormsFailure,
  fetchMissingItemFormSuccess,
  fetchMissingItemFormFailure,
} from '../slices';
import { setMissingItemFormFields } from '@/forms/store';
import { adaptFormItemArrayResponseToFormFields } from '@/adapters';
import { MissingItemApi } from '@/api';
import { toStoreError } from '@/types/storeError.interface';
import type { UpdateMissingItemFormFieldRequestDTO } from '@/api/missing-item-form/DTOs';
import type {
  UpdateSectionConditionalFieldsGroupRequestDTO,
  UpdateStagedFieldsGroupRequestDTO,
} from '@/api/section/DTOs';
import { updateFieldFailedAlert } from '@/services/alert';
import {
  notifyFetched,
  tookEffectDespite,
  type GroupActionOutcome,
  type OnFormFetched,
} from './groupActionOutcome';

/** Options accepted by fetches that can run behind an already-rendered missing item form. */
export type MissingItemFormFetchOptions = {
  background?: boolean;
  onFetched?: OnFormFetched;
};

export const fetchUserMissingItemForms =
  () => async (dispatch: AppDispatch) => {
    dispatch(userMissingItemFormsLoading());

    try {
      const result = await new MissingItemApi().getUserMissingItemForms();

      if (result.success) {
        const forms = result.data?.missingItemForms ?? [];
        dispatch(fetchUserMissingItemFormsSuccess(forms));
      } else {
        dispatch(fetchUserMissingItemFormsFailure(toStoreError(result.error)));
      }
    } catch (e) {
      console.error(e);
      const error = e as Error;
      dispatch(
        fetchUserMissingItemFormsFailure({
          code: 'UNK_001',
          message: error.message,
          category: 'server',
        }),
      );
    }
  };

export const fetchMissingItemForm =
  (id: number, options: MissingItemFormFetchOptions = {}) =>
  async (dispatch: AppDispatch) => {
    // Background refreshes deliberately leave `loading` alone: flipping it swaps the
    // form for a skeleton, which remounts DynamicForm and resets the reinitialize counter.
    if (!options.background) {
      dispatch(missingItemFormLoading());
    }

    try {
      const result = await new MissingItemApi().getMissingItemForm(id);

      if (result.success && result.data != null) {
        const formFields = adaptFormItemArrayResponseToFormFields(
          result.data.fields ?? [],
        );
        notifyFetched(options.onFetched, formFields);
        dispatch(
          setMissingItemFormFields({
            missingItemFormId: result.data.id,
            formFields,
          }),
        );
        dispatch(
          fetchMissingItemFormSuccess({
            id: result.data.id,
            background: options.background,
          }),
        );
      } else {
        notifyFetched(options.onFetched, undefined);
        dispatch(
          fetchMissingItemFormFailure(
            result.success
              ? { code: 'UNK_001', message: 'Form not found', category: 'server' }
              : toStoreError(result.error),
          ),
        );
      }
    } catch (e) {
      console.error(e);
      const error = e as Error;
      dispatch(
        fetchMissingItemFormFailure({
          code: 'UNK_001',
          message: error.message,
          category: 'server',
        }),
      );
    }
  };

export const confirmMissingItemReview =
  (missingItemFormId: number) => async (dispatch: AppDispatch) => {
    const result = await new MissingItemApi().confirmMissingItemForm(
      missingItemFormId,
    );

    if (!result.success) {
      dispatch(fetchMissingItemFormFailure(toStoreError(result.error)));
      return false;
    }

    await dispatch(fetchUserMissingItemForms());
    return true;
  };

export const updateMissingItemFormField =
  (
    missingItemFormId: number,
    formField: UpdateMissingItemFormFieldRequestDTO,
  ) =>
  async (dispatch: AppDispatch): Promise<boolean | undefined> => {
    const result = await new MissingItemApi().updateMissingItemFormField(
      missingItemFormId,
      formField,
    );

    if (!result.success) {
      dispatch(fetchMissingItemFormFailure(toStoreError(result.error)));
      return;
    }

    const response = result.data;
    if (!response.success) {
      updateFieldFailedAlert();
      dispatch(
        fetchMissingItemFormFailure({
          code: 'UNK_001',
          message: `Failed to update form field ${formField.itemID}`,
          category: 'server',
        }),
      );
    } else {
      return response.reloadRequired;
    }
  };

export const addMissingItemFormGroup =
  (missingItemFormId: number, groupId: string) =>
  async (dispatch: AppDispatch) => {
    const result = await new MissingItemApi().addMissingItemFormGroup(
      missingItemFormId,
      groupId,
    );

    if (!result.success) {
      dispatch(fetchMissingItemFormFailure(toStoreError(result.error)));
      return;
    }

    dispatch(fetchMissingItemForm(missingItemFormId));
  };

/** Removes a group entry, reporting to `outcome` whether the server took it. */
export const removeMissingItemFormGroup =
  (missingItemFormId: number, groupId: string, outcome?: GroupActionOutcome) =>
  async (dispatch: AppDispatch) => {
    const result = await new MissingItemApi().removeMissingItemFormGroup(
      missingItemFormId,
      groupId,
    );

    if (!result.success) {
      const refetch = (onFetched: OnFormFetched) =>
        fetchMissingItemForm(missingItemFormId, { onFetched });
      if (await tookEffectDespite(result.error, dispatch, refetch, outcome)) return;
      dispatch(fetchMissingItemFormFailure(toStoreError(result.error)));
      return;
    }

    // False when the server found nothing to remove, and so removed nothing.
    if (result.data !== true) {
      updateFieldFailedAlert();
      dispatch(
        fetchMissingItemFormFailure({
          code: 'UNK_001',
          message: `Failed to remove group ${groupId}`,
          category: 'server',
        }),
      );
      return;
    }

    outcome?.onAccepted();
    dispatch(fetchMissingItemForm(missingItemFormId));
  };

/** Clears a group entry, reporting to `outcome` whether the server took it. */
export const clearMissingItemFormGroup =
  (missingItemFormId: number, groupId: string, outcome?: GroupActionOutcome) =>
  async (dispatch: AppDispatch) => {
    const result = await new MissingItemApi().clearMissingItemFormGroup(
      missingItemFormId,
      groupId,
    );

    if (!result.success) {
      const refetch = (onFetched: OnFormFetched) =>
        fetchMissingItemForm(missingItemFormId, { onFetched });
      if (await tookEffectDespite(result.error, dispatch, refetch, outcome)) return;
      dispatch(fetchMissingItemFormFailure(toStoreError(result.error)));
      return;
    }

    const response = result.data;
    if (!response.success) {
      updateFieldFailedAlert();
      dispatch(
        fetchMissingItemFormFailure({
          code: 'UNK_001',
          message: `Failed to clear group ${groupId}`,
          category: 'server',
        }),
      );
    } else {
      outcome?.onAccepted();
      dispatch(fetchMissingItemForm(missingItemFormId));
    }
  };

export const updateMissingItemFormStagedFieldsGroup =
  (
    missingItemFormId: number,
    formField: UpdateStagedFieldsGroupRequestDTO['formField'],
  ) =>
  async (dispatch: AppDispatch): Promise<boolean | undefined> => {
    const result =
      await new MissingItemApi().updateMissingItemFormStagedFieldsGroup(
        missingItemFormId,
        formField,
      );

    if (!result.success) {
      dispatch(fetchMissingItemFormFailure(toStoreError(result.error)));
      return;
    }

    const response = result.data;
    if (!response.success) {
      updateFieldFailedAlert();
      dispatch(
        fetchMissingItemFormFailure({
          code: 'UNK_001',
          message: `Failed to update StagedGroup ${formField.groupID}`,
          category: 'server',
        }),
      );
    } else {
      return response.reloadRequired;
    }
  };

export const updateMissingItemFormConditionalFieldsGroup =
  (
    missingItemFormId: number,
    formField: UpdateSectionConditionalFieldsGroupRequestDTO['formField'],
  ) =>
  async (dispatch: AppDispatch): Promise<boolean | undefined> => {
    const result =
      await new MissingItemApi().updateMissingItemFormConditionalFieldsGroup(
        missingItemFormId,
        formField,
      );

    if (!result.success) {
      dispatch(fetchMissingItemFormFailure(toStoreError(result.error)));
      return;
    }

    const response = result.data;
    if (!response.success) {
      updateFieldFailedAlert();
      dispatch(
        fetchMissingItemFormFailure({
          code: 'UNK_001',
          message: `Failed to update conditional group ${formField.groupID}`,
          category: 'server',
        }),
      );
    } else {
      return response.reloadRequired;
    }
  };
